import * as React from 'react'
import {
  Box,
  Stack,
  FormControlLabel,
  Checkbox,
  Button,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import 'dayjs/locale/he'
import AppDateRangePicker from '@/components/DatePickers/AppDateRangePicker'
import MuteBellIcon from '@assets/ConsumersNotifications/MuteBell.svg'
import UnMuteBellIcon from '@assets/ConsumersNotifications/UnMute.svg'
import { useAppTheme } from '@/theme/ThemeContext'

/** The meter page's filters read as date and time. */
export const ALERTS_DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm'

export type AlertsFilters = {
  from: string
  to: string
  showClosed: boolean
  showNotification: boolean
}

type Props = {
  value?: AlertsFilters
  defaultValue?: AlertsFilters
  onChange?: (f: AlertsFilters) => void
  onSearch?: (f: AlertsFilters) => void
  onOpenNotifications?: (anchorEl: HTMLElement) => void
  alertsMode?: 'enabled' | 'muted'
  disabled?: boolean
  showNotificationVisible?: boolean
  showCheckBox: boolean
  openManagePopover?: HTMLElement | null
  /** 'meterPage' drops the bar's bottom line and filters by date and time, dd/mm/yyyy HH:mm. */
  variant?: 'default' | 'meterPage'
}

const AlertsFiltersBar: React.FC<Props> = ({
  value,
  defaultValue = {
    from: '',
    to: '',
    showClosed: false,
    showNotification: true,
  },
  onChange,
  onSearch,
  onOpenNotifications,
  alertsMode = 'enabled',
  disabled = false,
  showNotificationVisible = true,
  showCheckBox = true,
  openManagePopover = null,
  variant = 'default',
}) => {
  const meterPage = variant === 'meterPage'
  const valueFormat = meterPage ? ALERTS_DATE_TIME_FORMAT : 'DD/MM/YYYY'
  const parseFilterDate = (text: string) =>
    text ? dayjs(text, valueFormat).toDate() : null
  const formatFilterDate = (date: Date | null) =>
    date ? dayjs(date).format(valueFormat) : ''
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const colors = {
    text: isDarkMode ? '#fff' : '#003F61',
    border: isDarkMode ? '#445876' : '#E6EEF5',
    bellBg: isDarkMode ? '#202B40' : '#FFF',
    bellBgActive: isDarkMode ? '#34507D' : '#E3F5FF',
    bellBorder: isDarkMode ? '#445876' : '#E4E7EB',
    btnBg: isDarkMode ? '#003089' : '#E3F5FF',
    btnHover: isDarkMode ? '#2563EB' : '#CBE0EF',
    btnText: isDarkMode ? '#fff' : '#003F61',
  }

  const [internal, setInternal] = React.useState<AlertsFilters>(defaultValue)
  const filters = value ?? internal
  const shouldShowBell = showNotificationVisible ?? !!filters.showNotification

  const update = (patch: Partial<AlertsFilters>) => {
    const next = { ...filters, ...patch }
    if (!value) setInternal(next)
    onChange?.(next)
  }

  return (
  <Box sx={{ width: '100%', flexShrink: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{
          py: 1.25,
          px: 2,
          borderBottom: meterPage ? 'none' : `1px solid ${colors.border}`,
          flexWrap: 'wrap',
          rowGap: 1,
        }}
      >
        {shouldShowBell && (
          <Box sx={{ mr: 'auto' }}>
            <Tooltip title="ניהול הודעות לצרכן">
              <Box
                onClick={e => onOpenNotifications?.(e.currentTarget)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  width: 'clamp(40px, 3vw, 52px)',
                  height: 'clamp(40px, 3vw, 52px)',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '4.5px',
                  border: `1px solid ${colors.bellBorder}`,
                  background: openManagePopover !== null ? colors.bellBgActive : colors.bellBg,
                }}
              >
                <img
                  src={alertsMode === 'muted' ? MuteBellIcon : UnMuteBellIcon}
                  alt="Bell"
                  style={{ filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }}
                />
              </Box>
            </Tooltip>
          </Box>
        )}

        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{ flex: 1, minWidth: 0, flexWrap: 'wrap', rowGap: 1, justifyContent: 'flex-end' }}
        >
          <Button
            onClick={() => onSearch?.(filters)}
            disabled={disabled}
            sx={{
              minWidth: 'clamp(76px, 6vw, 108px)',
              height: 'clamp(34px, 2.8vw, 46px)',
              borderRadius: '4.5px',
              bgcolor: colors.btnBg,
              color: colors.text,
              fontWeight: 700,
              '&:hover': { bgcolor: colors.btnHover },
            }}
          >
              <Typography sx={{ color: colors.text, fontFamily: 'Rubik', fontSize: 'clamp(12px, 0.9vw, 16px)', fontWeight: 300 }}>
              חיפוש
            </Typography>
          </Button>

          {showCheckBox && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!filters.showClosed}
                  onChange={e => update({ showClosed: e.target.checked })}
                  disabled={disabled}
                  sx={{ color: colors.text }}
                />
              }
              label="הצגת התראות סגורות"
              sx={{
                mr: 1,
                direction: 'rtl',
                color: colors.text,
                ...(meterPage && { '& .MuiFormControlLabel-label': { fontSize: '12px' } }),
              }}
            />
          )}

          <AppDateRangePicker
            value={{
              from: parseFilterDate(filters.from),
              to: parseFilterDate(filters.to),
            }}
            onChange={range =>
              update({
                from: formatFilterDate(range.from),
                to: formatFilterDate(range.to),
              })
            }
            withTime={meterPage}
            disableFuture={!meterPage}
            clearable
            width="clamp(150px, 14vw, 260px)"
          />
        </Stack>
      </Stack>
    </Box>
  )
}

export default AlertsFiltersBar
